import type { ChildHealthView } from '@wodi/shared';
import { SearchPicker } from '@wodi/ui';
import { useState } from 'react';

import styles from './maternity.module.css';

// Type-to-search for the vaccine of a dose (DESIGN.md: no long dropdowns for clinical
// choices; 44px rows from SearchPicker). The facility's vaccines are few and already loaded,
// so it filters them here, in their set order, by name or code.

type Vaccine = ChildHealthView['vaccines'][number];

export function matchVaccines(vaccines: readonly Vaccine[], text: string): Vaccine[] {
  const q = text.trim().toLowerCase();
  if (q === '') return [...vaccines];
  const words = q.split(/\s+/);
  return vaccines.filter((v) => {
    const hay = `${v.display} ${v.code}`.toLowerCase();
    return words.every((w) => hay.includes(w));
  });
}

export function VaccinePicker({
  vaccines,
  value,
  onChange,
}: {
  vaccines: readonly Vaccine[];
  value: string;
  onChange: (vaccineId: string) => void;
}) {
  const chosen = vaccines.find((v) => v.id === value);
  const [text, setText] = useState(chosen?.display ?? '');
  const items = matchVaccines(vaccines, text === chosen?.display ? '' : text);
  return (
    <SearchPicker
      label="Vaccine"
      placeholder="Type the vaccine name or code"
      inputValue={text}
      onInputChange={(next) => {
        setText(next);
        if (next !== chosen?.display) onChange('');
      }}
      status="ready"
      minChars={0}
      items={items}
      getKey={(v) => v.id}
      getTextValue={(v) => v.display}
      renderItem={(v) => (
        <span>
          {v.display} <span className={`wodi-id ${styles.meta}`}>{v.code}</span>
        </span>
      )}
      onSelect={(v) => {
        setText(v.display);
        onChange(v.id);
      }}
      isExactMatch={(v, q) => v.code.toLowerCase() === q.trim().toLowerCase()}
      emptyMessage="No vaccine set up here matches. Check the spelling or search by code."
    />
  );
}
